import interpolateComponents from '@automattic/interpolate-components';

import { ApiInvite } from 'api/invite/types/ApiInvite';
import { Box } from 'components/Box';
import { AppleButton, FacebookButton, GoogleButton } from 'components/Button';
import { Divider } from 'components/Divider';
import { Form, FormUsageContext } from 'components/Form/Form';
import {
  FormMapErrorFunction,
  FormSubmitFunction,
} from 'components/Form/FormContext';
import { GeneralError } from 'components/Form/GeneralError';
import { InputField } from 'components/Form/InputField';
import { LocationField } from 'components/Form/LocationField';
import { RecaptchaField } from 'components/Form/RecaptchaField';
import { SubmitButton } from 'components/Form/SubmitButton/SubmitButton';
import { InviteMessage } from 'components/InviteMessage';
import { BodySmall } from 'components/Text/BodySmall';
import { SearchLocation } from 'modules/search/types/SearchLocation';
import { deleteCookie } from 'utils/cookies';

import { SignupFormLoginLink } from './SignupFormLoginLink';

// TODO: add proper types to SignupFormValues
export type SignupFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  location: SearchLocation | null;
  recaptchaKey: number;
  gRecaptchaResponse: unknown;
};

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  initialValues: Partial<SignupFormValues>;
  invite?: ApiInvite | null | undefined;
  showLoginModal?: (email: string) => void;
  getLoginUrlAndState: (email?: string) => {
    to: string;
    state: Record<string, unknown>;
  };
  getThirdPartyLoginUrl: (
    provider: 'facebook' | 'google' | 'apple',
  ) => Promise<string> | string;
  onSubmit: FormSubmitFunction<SignupFormValues>;
};

export function SignupForm({
  analyticsTitle,
  usageContext,
  initialValues,
  invite,
  showLoginModal,
  getLoginUrlAndState,
  getThirdPartyLoginUrl,
  onSubmit,
}: Props) {
  const emailErrorWithLogin = (email: string) =>
    interpolateComponents({
      mixedString: getText(
        'Email already in use - Please {{link}}login{{/link}}',
      ),
      components: {
        link: (
          <SignupFormLoginLink
            formUsageContext={usageContext}
            email={email}
            getLoginUrlAndState={getLoginUrlAndState}
            showLoginModal={showLoginModal}
            data-qa-id="email-error-login-link"
          />
        ),
      },
    });

  const mapError: FormMapErrorFunction<SignupFormValues> = ({
    name,
    value,
    type,
    description,
  }) =>
    name === 'email' && type === 'alreadyInUse'
      ? emailErrorWithLogin(typeof value === 'string' ? value : '')
      : description;

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      data-qa-id="signup-form"
      initialValues={initialValues}
      mapError={mapError}
      onSubmit={onSubmit}
      render={({ values }) => (
        <>
          {invite && (
            <BodySmall mt={3} mb={4}>
              {invite.hooks.map((hook) => (
                <InviteMessage
                  key={hook.hookType}
                  invite={invite}
                  hook={hook}
                />
              ))}
            </BodySmall>
          )}

          <Box mb={[16, null, 16]}>
            <BodySmall textAlign="center">
              {getText('Already have an account?')}{' '}
              <SignupFormLoginLink
                data-qa-id="signup-form-login-link"
                email={values.email}
                getLoginUrlAndState={getLoginUrlAndState}
                showLoginModal={showLoginModal}
                formUsageContext={usageContext}
              >
                {getText('Log In')}
              </SignupFormLoginLink>
            </BodySmall>
          </Box>

          <Box display="flex" flexDirection="column" mb={4} mt="-6px" mx="-6px">
            <Box m="6px">
              <GoogleButton
                onClick={async (e) => {
                  deleteCookie('userDismissedNotificationPreferencesDialog');
                  e.preventDefault();
                  const url = await getThirdPartyLoginUrl('google');
                  // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(string |... Remove this comment to see the full error message
                  window.location = url;
                }}
                data-qa-id="google-oauth"
              />
            </Box>
            <Box m="6px">
              <FacebookButton
                onClick={async (e) => {
                  deleteCookie('userDismissedNotificationPreferencesDialog');
                  e.preventDefault();
                  const url = await getThirdPartyLoginUrl('facebook');
                  // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(string |... Remove this comment to see the full error message
                  window.location = url;
                }}
                data-qa-id="facebook-oauth"
              />
            </Box>
            <Box m="6px">
              <AppleButton
                onClick={async (e) => {
                  deleteCookie('userDismissedNotificationPreferencesDialog');
                  e.preventDefault();
                  const url = await getThirdPartyLoginUrl('apple');
                  // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(string |... Remove this comment to see the full error message
                  window.location = url;
                }}
                data-qa-id="apple-oauth"
              />
            </Box>
          </Box>

          <Divider marginTop={24} marginBottom={24} />

          <GeneralError />

          <Box display="flex" flexWrap="wrap" mx={-12}>
            <Box px={12} width={[1, 1 / 2]} flex="0 0 auto">
              <InputField
                dataModalFocus={usageContext === 'modal' ? 'true' : undefined}
                name="firstName"
                label={getText('First Name')}
                placeholder={getText('Casey')}
              />
            </Box>

            <Box px={12} width={[1, 1 / 2]} flex="0 0 auto">
              <InputField
                name="lastName"
                label={getText('Last Name')}
                placeholder={getText('Smith')}
              />
            </Box>
          </Box>

          <InputField
            name="email"
            type="email"
            label={getText('Email')}
            placeholder={getText('casey.smith@example.com')}
          />

          <InputField
            name="password"
            type="password"
            label={getText('Password')}
            placeholder={getText('Password')}
          />

          <LocationField
            name="location"
            label={
              /* TRANSLATORS: where is the user located? e.g. New York */
              getText('Location')
            }
            customErrorMatch={(errors) =>
              errors && (errors.latitude || errors.longitude)
            }
          />

          <RecaptchaField />

          <Box mt={[16, null, 40]} display="flex" justifyContent="flex-end">
            <SubmitButton fullWidth size="large">
              {getText('Sign Up')}
            </SubmitButton>
          </Box>
        </>
      )}
    />
  );
}
